// Tenant theme utilities — flat design language per tenant.
//
// Each tenant has up to four brand colours (Primary / Secondary / Tertiary /
// Other). Rather than mixing those colours through the UI with rgba washes
// and gradients, we derive a LADDER of solid shades for each one and hand
// every CSS token a pure brand value. Buttons fill solid primary, soft
// surfaces show a solid pale-primary tint, hover/active states use a solid
// deeper primary — nothing is alpha-blended against an arbitrary background
// and nothing cross-mixes two different tenant colours.

function normalizeHex(input) {
  if (!input) return null;
  const raw = `${input}`.trim();
  if (!raw) return null;
  let value = raw.startsWith("#") ? raw.slice(1) : raw;
  if (value.length === 3) {
    value = value
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (!/^[0-9a-fA-F]{6}$/.test(value)) return null;
  return `#${value.toLowerCase()}`;
}

function hexToRgb(hex) {
  const normalized = normalizeHex(hex);
  if (!normalized) return null;
  const value = normalized.slice(1);
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }) {
  const toHex = (v) => {
    const clamped = Math.max(0, Math.min(255, Math.round(v)));
    return clamped.toString(16).padStart(2, "0");
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// weight=0 returns `hex`, weight=1 returns `targetHex`.
function mix(hex, targetHex, weight) {
  const a = hexToRgb(hex);
  const b = hexToRgb(targetHex);
  if (!a || !b) return hex;
  return rgbToHex({
    r: a.r * (1 - weight) + b.r * weight,
    g: a.g * (1 - weight) + b.g * weight,
    b: a.b * (1 - weight) + b.b * weight,
  });
}

function luminance(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const channel = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
}

function pickForeground(hex) {
  return luminance(hex) > 0.55 ? "#11131a" : "#ffffff";
}

// Build a solid-colour ladder off a single brand colour. Each rung is a
// concrete hex — no alpha, no cross-mixing with a different tenant colour.
// 0 → 4% black (darkest). 1.0 → 96% white (lightest).
function buildLadder(hex) {
  return {
    darker: mix(hex, "#000000", 0.28),
    dark: mix(hex, "#000000", 0.14),
    base: hex,
    light: mix(hex, "#ffffff", 0.78),
    lighter: mix(hex, "#ffffff", 0.9),
    lightest: mix(hex, "#ffffff", 0.96),
    rgb: (() => {
      const rgb = hexToRgb(hex);
      return rgb ? `${rgb.r} ${rgb.g} ${rgb.b}` : null;
    })(),
    foreground: pickForeground(hex),
  };
}

export function buildTenantThemeVars(appearance = {}) {
  const primary = normalizeHex(appearance.primaryColor);
  const secondary = normalizeHex(appearance.secondaryColor);
  const tertiary = normalizeHex(appearance.tertiaryColor);
  const other = normalizeHex(appearance.otherColor);
  const link = normalizeHex(appearance.linkColor);

  const vars = {};

  if (primary) {
    const p = buildLadder(primary);

    // Core primary token + solid ladder. We mirror these into a dedicated
    // `--tenant-primary-*` namespace so components can opt into tenant
    // theming with `var(--tenant-primary, <original-default>)` — a
    // tenant with no colors set never pollutes unrelated chrome.
    vars["--primary"] = p.base;
    vars["--primary-foreground"] = p.foreground;
    vars["--primary-rgb"] = p.rgb;
    vars["--primary-darker"] = p.darker;
    vars["--primary-dark"] = p.dark;
    vars["--primary-light"] = p.light;
    vars["--primary-lighter"] = p.lighter;
    vars["--primary-lightest"] = p.lightest;

    vars["--tenant-primary"] = p.base;
    vars["--tenant-primary-foreground"] = p.foreground;
    vars["--tenant-primary-rgb"] = p.rgb;
    vars["--tenant-primary-darker"] = p.darker;
    vars["--tenant-primary-dark"] = p.dark;
    vars["--tenant-primary-light"] = p.light;
    vars["--tenant-primary-lighter"] = p.lighter;
    vars["--tenant-primary-lightest"] = p.lightest;
    vars["--tenant-primary-soft"] = p.lighter;
    vars["--tenant-primary-soft-strong"] = p.light;

    // Soft fills and rings — solid, not alpha blends, so they never wash
    // out against whatever surface sits underneath.
    vars["--primary-soft"] = p.lighter;
    vars["--primary-soft-strong"] = p.light;
    vars["--ring"] = p.base;

    // Uniflo brand aliases map straight to the solid ladder.
    vars["--uniflo-brand"] = p.base;
    vars["--uniflo-brand-deep"] = p.darker;
    vars["--uniflo-brand-soft"] = p.lighter;
    vars["--uniflo-accent"] = p.base;
    vars["--uniflo-accent-soft"] = p.lighter;
    vars["--uniflo-accent-muted"] = p.lightest;

    // App surfaces — solid pale-primary tints so the whole page feels on
    // brand without mixing greys.
    vars["--app-shell"] = p.lightest;
    vars["--app-shell-strong"] = p.lighter;
    vars["--surface-soft"] = p.lightest;
    vars["--surface-subtle"] = p.lightest;
    vars["--surface-tint"] = p.lighter;
    vars["--surface-contrast"] = p.light;

    // Borders and inputs in solid shades.
    vars["--border"] = p.light;
    vars["--border-strong"] = p.base;
    vars["--input"] = p.lighter;
    vars["--uniflo-border"] = p.light;
    vars["--uniflo-line"] = p.light;
    vars["--uniflo-line-strong"] = p.base;

    // Sidebar / clan rail in solid pale primary with primary active state.
    vars["--sidebar-background"] = p.lightest;
    vars["--sidebar-foreground"] = "#11131a";
    vars["--sidebar-primary"] = p.base;
    vars["--sidebar-primary-foreground"] = p.foreground;
    vars["--sidebar-accent"] = p.lighter;
    vars["--sidebar-accent-foreground"] = p.darker;
    vars["--sidebar-border"] = p.light;
    vars["--sidebar-ring"] = p.base;

    // Glow / gradient tokens — now solid, not blended. Keep the var names
    // so existing CSS keeps working; just strip the gradient.
    vars["--canvas-glow"] = p.lightest;
    vars["--hero-glow"] = "none";
    vars["--brand-gradient"] = p.base;
    vars["--brand-gradient-soft"] = p.lightest;

    vars["--chart-1"] = p.base;

    // Board deck "Open" state fills with solid tenant Primary.
    vars["--deck-header-open"] = p.base;
    // Progress / Done default to Primary until tenant sets Secondary /
    // Tertiary; they get overridden below if those are configured.
    vars["--deck-header-progress"] = p.base;
    vars["--deck-header-done"] = p.base;
  }

  // Secondary → accent / info / secondary-action tokens, all solid.
  if (secondary) {
    const s = buildLadder(secondary);
    vars["--tenant-secondary"] = s.base;
    vars["--tenant-secondary-rgb"] = s.rgb;
    vars["--tenant-secondary-darker"] = s.darker;
    vars["--tenant-secondary-dark"] = s.dark;
    vars["--tenant-secondary-light"] = s.light;
    vars["--tenant-secondary-lighter"] = s.lighter;
    vars["--tenant-secondary-lightest"] = s.lightest;
    vars["--tenant-secondary-soft"] = s.lighter;
    vars["--tenant-secondary-foreground"] = s.foreground;

    vars["--accent"] = s.lighter;
    vars["--accent-foreground"] = s.darker;
    vars["--secondary"] = s.lighter;
    vars["--secondary-foreground"] = s.darker;
    vars["--info"] = s.base;
    vars["--info-foreground"] = s.foreground;
    vars["--info-soft"] = s.lighter;
    vars["--chart-2"] = s.base;

    // Progress board state fills with solid tenant Secondary.
    vars["--deck-header-progress"] = s.base;
  }

  // Tertiary → success / highlight tokens, all solid.
  if (tertiary) {
    const t = buildLadder(tertiary);
    vars["--tenant-tertiary"] = t.base;
    vars["--tenant-tertiary-rgb"] = t.rgb;
    vars["--tenant-tertiary-darker"] = t.darker;
    vars["--tenant-tertiary-dark"] = t.dark;
    vars["--tenant-tertiary-light"] = t.light;
    vars["--tenant-tertiary-lighter"] = t.lighter;
    vars["--tenant-tertiary-lightest"] = t.lightest;
    vars["--tenant-tertiary-soft"] = t.lighter;
    vars["--tenant-tertiary-foreground"] = t.foreground;

    vars["--success"] = t.base;
    vars["--success-foreground"] = t.foreground;
    vars["--success-soft"] = t.lighter;
    vars["--chart-3"] = t.base;

    // Done board state fills with solid tenant Tertiary.
    vars["--deck-header-done"] = t.base;
  }

  // Other → warning / emphasis tokens, all solid.
  if (other) {
    const o = buildLadder(other);
    vars["--tenant-other"] = o.base;
    vars["--tenant-other-rgb"] = o.rgb;
    vars["--tenant-other-darker"] = o.darker;
    vars["--tenant-other-dark"] = o.dark;
    vars["--tenant-other-light"] = o.light;
    vars["--tenant-other-lighter"] = o.lighter;
    vars["--tenant-other-lightest"] = o.lightest;
    vars["--tenant-other-soft"] = o.lighter;
    vars["--tenant-other-foreground"] = o.foreground;

    vars["--warning"] = o.base;
    vars["--warning-foreground"] = o.foreground;
    vars["--warning-soft"] = o.lighter;
    vars["--chart-4"] = o.base;
  }

  if (link) {
    vars["--tenant-link"] = link;
  }

  return vars;
}

const MANAGED_VARS = [
  "--primary",
  "--primary-foreground",
  "--primary-rgb",
  "--primary-darker",
  "--primary-dark",
  "--primary-light",
  "--primary-lighter",
  "--primary-lightest",
  "--primary-soft",
  "--primary-soft-strong",
  "--ring",
  "--chart-1",
  "--chart-2",
  "--chart-3",
  "--chart-4",
  "--uniflo-brand",
  "--uniflo-brand-deep",
  "--uniflo-brand-soft",
  "--uniflo-accent",
  "--uniflo-accent-soft",
  "--uniflo-accent-muted",
  "--uniflo-border",
  "--uniflo-line",
  "--uniflo-line-strong",
  "--app-shell",
  "--app-shell-strong",
  "--surface-soft",
  "--surface-subtle",
  "--surface-tint",
  "--surface-contrast",
  "--border",
  "--border-strong",
  "--input",
  "--sidebar-background",
  "--sidebar-foreground",
  "--sidebar-primary",
  "--sidebar-primary-foreground",
  "--sidebar-accent",
  "--sidebar-accent-foreground",
  "--sidebar-border",
  "--sidebar-ring",
  "--canvas-glow",
  "--hero-glow",
  "--brand-gradient",
  "--brand-gradient-soft",
  "--accent",
  "--accent-foreground",
  "--secondary",
  "--secondary-foreground",
  "--info",
  "--info-foreground",
  "--info-soft",
  "--success",
  "--success-foreground",
  "--success-soft",
  "--warning",
  "--warning-foreground",
  "--warning-soft",
  "--tenant-secondary",
  "--tenant-secondary-rgb",
  "--tenant-secondary-darker",
  "--tenant-secondary-dark",
  "--tenant-secondary-light",
  "--tenant-secondary-lighter",
  "--tenant-secondary-lightest",
  "--tenant-secondary-soft",
  "--tenant-secondary-foreground",
  "--tenant-tertiary",
  "--tenant-tertiary-rgb",
  "--tenant-tertiary-darker",
  "--tenant-tertiary-dark",
  "--tenant-tertiary-light",
  "--tenant-tertiary-lighter",
  "--tenant-tertiary-lightest",
  "--tenant-tertiary-soft",
  "--tenant-tertiary-foreground",
  "--tenant-other",
  "--tenant-other-rgb",
  "--tenant-other-darker",
  "--tenant-other-dark",
  "--tenant-other-light",
  "--tenant-other-lighter",
  "--tenant-other-lightest",
  "--tenant-other-soft",
  "--tenant-other-foreground",
  "--tenant-link",
  "--tenant-primary",
  "--tenant-primary-foreground",
  "--tenant-primary-rgb",
  "--tenant-primary-darker",
  "--tenant-primary-dark",
  "--tenant-primary-light",
  "--tenant-primary-lighter",
  "--tenant-primary-lightest",
  "--tenant-primary-soft",
  "--tenant-primary-soft-strong",
  "--deck-header-open",
  "--deck-header-progress",
  "--deck-header-done",
];

export function applyTenantTheme(appearance = {}, target = null) {
  if (typeof document === "undefined") return;
  const root = target || document.documentElement;
  if (!root) return;

  const vars = buildTenantThemeVars(appearance);

  MANAGED_VARS.forEach((name) => {
    if (!(name in vars)) {
      root.style.removeProperty(name);
    }
  });

  Object.entries(vars).forEach(([name, value]) => {
    if (value == null) return;
    root.style.setProperty(name, value);
  });
}
